import { Injectable } from '@angular/core';

import { ModalController } from '@ionic/angular/standalone';

import { AlertModalContentErrorsComponent, AlertModalTypedErrorsComponent } from '../../../components';
import { GuardFinderError } from '../Domain/guard-finder-error';

/**
 * Service for displaying errors throughout the application.
 *
 * This service provides methods for:
 * - Displaying error modals with typed errors (GuardFinderError)
 * - Displaying error modals with simple messages
 * - Displaying form validation errors
 */
@Injectable({
    providedIn: 'root'
})
export class ErrorDisplayService {
    /**
     * Creates an instance of ErrorService.
     *
     * @param modalCtrl - Controller for creating and managing modals
     */
    constructor(private modalCtrl: ModalController) {}

    /**
     * Displays a modal with typed errors (GuardFinderError[]).
     * This is the preferred method for showing errors in the application.
     *
     * @param errors - Array of GuardFinderError objects to display in the modal
     * @returns Promise that resolves when the modal is presented
     */
    async showTypedErrorsModal(errors: GuardFinderError[]): Promise<void> {
        const modal = await this.modalCtrl.create({
            component: AlertModalTypedErrorsComponent,
            componentProps: {
                errors
            },
            cssClass: 'modal-window'
        });

        await modal.present();
    }

    /**
     * Displays a modal with a single error message.
     * Converts the message to a GuardFinderError and uses the typed errors modal.
     *
     * @param message - Error message to display in the modal
     * @returns Promise that resolves when the modal is presented
     */
    async showErrorMessageModal(message: string): Promise<void> {
        const errors = [new GuardFinderError(message)];
        const modal = await this.modalCtrl.create({
            component: AlertModalTypedErrorsComponent,
            componentProps: {
                errors
            },
            cssClass: 'modal-window'
        });

        await modal.present();
    }

    /**
     * Displays a modal with untyped errors.
     *
     * @deprecated Use showTypedErrorsModal with GuardFinderError objects instead
     * @param errors - Error object or array to display in the modal
     * @returns Promise that resolves when the modal is created
     */
    async showErrorsModal(errors: unknown) {
        const modal = await this.modalCtrl.create({
            component: AlertModalContentErrorsComponent,
            componentProps: {
                errors
            },
            cssClass: 'modal-window'
        });

        modal.present().then();
    }
}
